import React, { useEffect, useState } from 'react';
import moment from 'moment';
import 'moment-timezone';

import RRuleEditor from './RRuleEditor';
import { useAuth } from './common/Auth';
import { useCurrentSystem } from './context/SystemContext';
import { getSceneType, MIN_CYCLE_HOLD_SECONDS } from './sceneUtils';
import { firestore } from './common/firebase';
import { collection, doc, addDoc, updateDoc, deleteField, Timestamp, serverTimestamp } from 'firebase/firestore';
import {
	ASTRO_EVENTS,
	hasAstroLocation,
	resolveAstroPerformAt,
	resolveAstroTargetDateForRefresh,
	formatAstroLabel,
	formatTimeInTz,
	getSystemTimezone,
} from './astroUtils';
import { canUseAiHelp } from './aiHelp/entitlement';
import { isAiHelpConfigured } from './aiHelp/api';
import { useAiEventSuggestions } from './aiHelp/useAiEventSuggestions';
import AiEventSuggestions from './aiHelp/AiEventSuggestions';
import { resolveSuggestionColors } from './aiHelp/resolveSuggestionColors';

function ScheduleEditor({ scenes = [], schedule, open, doneEdit }) {
	const currentUser = useAuth();
	const { currentSystem } = useCurrentSystem();
	const [values, setValues] = useState({ name: 'New Schedule', priority: 0 });
	const [isRepeating, setIsRepeating] = useState(false);
	const [r_rule, setR_Rule] = useState(undefined);
	const [selectedDate, handleDateChange] = useState(() => new Date());
	const [newSchedule, setNewSchedule] = useState(true);
	const [linkedSceneId, setLinkedSceneId] = useState('');
	const [timeMode, setTimeMode] = useState('fixed');
	const [astroEvent, setAstroEvent] = useState('sunset');
	const [astroOffsetMinutes, setAstroOffsetMinutes] = useState(0);
	const [saveError, setSaveError] = useState('');
	const [julesBusy, setJulesBusy] = useState(false);

	const julesEnabled = canUseAiHelp(currentUser)
		&& currentSystem?.aiHelpEnabled === true
		&& isAiHelpConfigured();
	const dateStart = moment(selectedDate).format('YYYY-MM-DD');
	const suggestions = useAiEventSuggestions({
		enabled: open && julesEnabled,
		queryName: values.name,
		dateStart,
		dateEnd: dateStart,
		system: currentSystem,
		numZones: currentSystem?.num_colors || 4,
		context: 'schedule',
	});

	useEffect(() => {
		if (schedule?.uid) {
			setValues(schedule);
			setNewSchedule(false);
			const repeating = schedule.repeats === true || !!(schedule.r_rule || schedule.rrule);
			setIsRepeating(repeating);
			if (repeating) {
				let rule = schedule.r_rule || schedule.rrule;
				if (rule) {
					rule = { ...rule };
					delete rule.dtstart;
					setR_Rule({ state: rule });
				}
			} else {
				setR_Rule(undefined);
			}
			setLinkedSceneId(schedule.scene || schedule.cycle || '');
			setTimeMode(schedule.timeMode === 'astro' ? 'astro' : 'fixed');
			setAstroEvent(schedule.astroEvent || 'sunset');
			setAstroOffsetMinutes(Number(schedule.astroOffsetMinutes) || 0);
			if (schedule.performAt) {
				const d = schedule.performAt.toDate
					? schedule.performAt.toDate()
					: new Date(schedule.performAt.seconds * 1000);
				handleDateChange(d);
			}
		} else {
			setNewSchedule(true);
			setValues({ name: schedule?.name || 'New Schedule', priority: 0 });
			setIsRepeating(false);
			setR_Rule(undefined);
			setLinkedSceneId('');
			setTimeMode('fixed');
			setAstroEvent('sunset');
			setAstroOffsetMinutes(0);
			if (schedule?.defaultPerformAt) {
				handleDateChange(new Date(schedule.defaultPerformAt));
			} else {
				handleDateChange(new Date());
			}
		}
		setSaveError('');
	}, [schedule]);

	const isAstro = timeMode === 'astro';
	const systemTz = getSystemTimezone(currentSystem);
	const astroPreview = isAstro && hasAstroLocation(currentSystem)
		? (() => {
			const draft = {
				timeMode: 'astro',
				astroEvent,
				astroOffsetMinutes,
				repeats: isRepeating,
				r_rule: r_rule?.state,
				performAt: Timestamp.fromDate(selectedDate),
			};
			const target = resolveAstroTargetDateForRefresh(draft, currentSystem, selectedDate);
			const resolved = resolveAstroPerformAt(draft, currentSystem, target.format('YYYY-MM-DD'));
			return resolved ? formatTimeInTz(resolved, systemTz) : null;
		})()
		: null;

	const saveSchedule = async () => {
		setSaveError('');

		if (isAstro && !hasAstroLocation(currentSystem)) {
			setSaveError('Set latitude and longitude in system settings before using astronomical times.');
			return;
		}

		const sch = {
			name: values.name,
			priority: values.priority ?? 0,
			system: currentSystem.uid,
			timeMode: isAstro ? 'astro' : 'fixed',
		};

		if (isAstro) {
			sch.astroEvent = astroEvent;
			sch.astroOffsetMinutes = Number(astroOffsetMinutes) || 0;
		}

		if (isAstro) {
			const draft = {
				...sch,
				repeats: isRepeating,
				r_rule: isRepeating && r_rule?.state ? r_rule.state : undefined,
				performAt: Timestamp.fromDate(selectedDate),
			};
			const target = resolveAstroTargetDateForRefresh(draft, currentSystem, selectedDate);
			const resolved = resolveAstroPerformAt(draft, currentSystem, target.format('YYYY-MM-DD'));
			if (!resolved) {
				setSaveError('Astronomical time unavailable for this date and location.');
				return;
			}
			sch.performAt = Timestamp.fromDate(resolved);
		} else {
			const fixed_date = new Date(
				selectedDate.getFullYear(),
				selectedDate.getMonth(),
				selectedDate.getDate(),
				selectedDate.getHours(),
				selectedDate.getMinutes(),
				selectedDate.getSeconds()
			);
			sch.performAt = Timestamp.fromDate(fixed_date);
		}

		sch.init = true;
		sch.scene = linkedSceneId || undefined;

		if (isRepeating && r_rule?.state) {
			const rule = { ...r_rule.state };
			delete rule.dtstart;
			sch.r_rule = rule;
			sch.repeats = true;
		} else {
			sch.repeats = false;
			if (newSchedule) {
				sch.status = 'scheduled';
			}
		}

		if (newSchedule) {
			await addDoc(collection(firestore, 'schedules'), sch);
		} else {
			const updateData = { ...sch };
			if (!isRepeating) {
				updateData.r_rule = deleteField();
			}
			if (!isAstro) {
				updateData.astroEvent = deleteField();
				updateData.astroOffsetMinutes = deleteField();
			}
			updateData.cycle = deleteField();
			await updateDoc(doc(firestore, 'schedules', schedule.uid), updateData);
		}
		doneEdit();
	};

	const handleChange = (name) => (event) => {
		setValues({ ...values, [name]: event.target.value });
	};

	const handleClose = () => doneEdit();

	const applyJulesName = (event) => {
		if (!event?.name) return;
		setValues((prev) => ({ ...prev, name: event.name }));
	};

	const createSceneAndLink = async (event) => {
		if (!currentSystem?.uid || !event?.name) return;
		setJulesBusy(true);
		setSaveError('');
		try {
			const numZones = currentSystem.num_colors || 4;
			const resolved = resolveSuggestionColors(event, numZones);
			const looks = resolved.type === 'cycle' && resolved.looks?.length
				? resolved.looks.map((l) => ({
					colors: l.colors || [],
					hold: l.hold || MIN_CYCLE_HOLD_SECONDS,
				}))
				: [{ colors: resolved.colors || [], hold: 0 }];
			const type = resolved.type === 'cycle' ? 'cycle' : 'single';
			const ref = await addDoc(collection(firestore, 'scenes'), {
				name: event.name,
				type,
				looks,
				system_id: currentSystem.uid,
				createdAt: serverTimestamp(),
			});
			setLinkedSceneId(ref.id);
			setValues((prev) => ({ ...prev, name: prev.name || event.name }));
		} catch (err) {
			setSaveError(err.message || 'Failed to create scene');
		} finally {
			setJulesBusy(false);
		}
	};

	const applyColorsToLinkedScene = async (event) => {
		if (!linkedSceneId) return;
		setJulesBusy(true);
		setSaveError('');
		try {
			const numZones = currentSystem?.num_colors || 4;
			const resolved = resolveSuggestionColors(event, numZones);
			const looks = resolved.type === 'cycle' && resolved.looks?.length
				? resolved.looks.map((l) => ({
					colors: l.colors || [],
					hold: l.hold || MIN_CYCLE_HOLD_SECONDS,
				}))
				: [{ colors: resolved.colors || [], hold: 0 }];
			const type = resolved.type === 'cycle' ? 'cycle' : 'single';
			await updateDoc(doc(firestore, 'scenes', linkedSceneId), {
				...(event.name ? { name: event.name } : {}),
				type,
				looks,
			});
		} catch (err) {
			setSaveError(err.message || 'Failed to update linked scene');
		} finally {
			setJulesBusy(false);
		}
	};

	if (!open) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
			<div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-xl w-full max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-auto">
				<div className="p-4 sm:p-6">
					<h3 className="font-semibold text-lg mb-4">Edit Schedule</h3>

					<div className="space-y-4">
						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Name</label>
							<input className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={values.name || ''} onChange={handleChange('name')} />
						</div>

						{julesEnabled && (
							<AiEventSuggestions
								loading={suggestions.loading || julesBusy}
								error={suggestions.error}
								events={suggestions.events}
								context="schedule"
								hasLinkedScene={!!linkedSceneId}
								onUseName={applyJulesName}
								onCreateSceneAndLink={createSceneAndLink}
								onApplyColorsToLinkedScene={applyColorsToLinkedScene}
							/>
						)}

						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Time type</label>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={() => setTimeMode('fixed')}
									className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors ${!isAstro ? 'bg-[#880e4f] text-white border-[#880e4f]' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
								>
									Fixed
								</button>
								<button
									type="button"
									onClick={() => setTimeMode('astro')}
									className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors ${isAstro ? 'bg-[#880e4f] text-white border-[#880e4f]' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
								>
									Astronomical
								</button>
							</div>
						</div>

						{isAstro ? (
							<>
								<div>
									<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Astronomical event</label>
									<select
										className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 text-sm"
										value={astroEvent}
										onChange={(e) => setAstroEvent(e.target.value)}
									>
										{ASTRO_EVENTS.map((ev) => (
											<option key={ev.key} value={ev.key}>{ev.label}</option>
										))}
									</select>
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Offset (minutes)</label>
									<input
										type="number"
										step="1"
										className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full"
										value={astroOffsetMinutes}
										onChange={(e) => setAstroOffsetMinutes(e.target.value === '' ? 0 : parseInt(e.target.value, 10))}
										placeholder="0"
									/>
									<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
										Negative = before event, positive = after. Example: −30 runs 30 minutes before {ASTRO_EVENTS.find((e) => e.key === astroEvent)?.label || 'event'}.
									</p>
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Start date</label>
									<input type="date" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={moment(selectedDate).format('YYYY-MM-DD')} onChange={(e) => {
										const d = new Date(selectedDate);
										const [y, m, da] = e.target.value.split('-');
										d.setFullYear(+y, +m - 1, +da);
										handleDateChange(d);
									}} />
								</div>
								{!hasAstroLocation(currentSystem) && (
									<p className="text-xs text-amber-600 dark:text-amber-400">
										Set latitude and longitude in system settings to use astronomical times.
									</p>
								)}
								{astroPreview && (
									<p className="text-xs text-gray-600 dark:text-gray-400">
										Resolved: {formatAstroLabel(astroEvent, astroOffsetMinutes)} at {astroPreview} ({systemTz})
									</p>
								)}
							</>
						) : (
							<div className="grid grid-cols-2 gap-4">
								<div>
									<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Date</label>
									<input type="date" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={moment(selectedDate).format('YYYY-MM-DD')} onChange={(e) => {
										const d = new Date(selectedDate);
										const [y, m, da] = e.target.value.split('-');
										d.setFullYear(+y, +m - 1, +da);
										handleDateChange(d);
									}} />
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Time</label>
									<input type="time" className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2 w-full" value={moment(selectedDate).format('HH:mm')} onChange={(e) => {
										const d = new Date(selectedDate);
										const [h, mi] = e.target.value.split(':');
										d.setHours(+h, +mi);
										handleDateChange(d);
									}} />
								</div>
							</div>
						)}

						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1.5">Repeat</label>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={() => setIsRepeating(false)}
									className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors ${!isRepeating ? 'bg-[#880e4f] text-white border-[#880e4f]' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
								>
									One-time
								</button>
								<button
									type="button"
									onClick={() => setIsRepeating(true)}
									className={`flex-1 px-3 py-2 text-sm rounded-xl border transition-colors ${isRepeating ? 'bg-[#880e4f] text-white border-[#880e4f]' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
								>
									Repeating
								</button>
							</div>
							{!isRepeating && (
								<p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
									{isAstro ? 'Runs once on the start date at the astronomical time.' : 'Runs once at the date and time above.'}
								</p>
							)}
						</div>

						{isRepeating && (
							<div>
								<RRuleEditor value={r_rule} onChange={setR_Rule} />
							</div>
						)}

						<div>
							<label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Scene</label>
							<select
								className="w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-700 focus:border-[#880e4f] rounded-xl px-4 py-2"
								value={linkedSceneId}
								onChange={(e) => setLinkedSceneId(e.target.value)}
							>
								<option value="">-- None (blackout) --</option>
								{(scenes || []).map((item) => (
									<option key={item.uid} value={item.uid}>
										{item.name}{getSceneType(item) === 'cycle' ? ' (cycle)' : ''}
									</option>
								))}
							</select>
						</div>

						{saveError && (
							<p className="text-xs text-red-600 dark:text-red-400">{saveError}</p>
						)}
					</div>
				</div>

				<div className="px-6 py-4 border-t flex gap-3 justify-end bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
					<button onClick={handleClose} className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600">Cancel</button>
					<button onClick={saveSchedule} className="px-4 py-2 text-sm bg-[#880e4f] text-white rounded-lg hover:bg-[#6b0b3f] active:bg-[#4a0835]">Save Schedule</button>
				</div>
			</div>
		</div>
	);
}

export default ScheduleEditor;
